import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { formatInterviewScrollHeader } from '../utils/formatDate';

type Options = {
    /** ISO timestamps used for sticky date fallback (first visible message). */
    messageTimestamps: (string | undefined)[];
    /** When any of these change, scroll the thread to the bottom (same as process interview). */
    scrollDeps: unknown[];
};

/**
 * Scroll + sticky date header for `.thread` chat panes (process interview modern layout).
 */
export function useInterviewThreadScroll({ messageTimestamps, scrollDeps }: Options) {
    const threadScrollRef = useRef<HTMLDivElement>(null);
    const [stickyDateLabel, setStickyDateLabel] = useState('');

    const scrollToBottom = useCallback(() => {
        const el = threadScrollRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, []);

    const updateScrollDateLabel = useCallback(() => {
        const container = threadScrollRef.current;
        if (!container) return;

        const nodes = container.querySelectorAll('[data-msg-timestamp]');
        const containerRect = container.getBoundingClientRect();
        const threshold = containerRect.top + 40;
        let chosen = '';

        for (const node of nodes) {
            const el = node as HTMLElement;
            if (el.getBoundingClientRect().top <= threshold) {
                chosen = el.getAttribute('data-msg-timestamp') || '';
            }
        }

        const firstTs = messageTimestamps.find(Boolean);
        if (!chosen && firstTs) {
            chosen = firstTs;
        }

        setStickyDateLabel(chosen ? formatInterviewScrollHeader(chosen) : '');
    }, [messageTimestamps]);

    const firstTimestamp = messageTimestamps.find(Boolean);
    const scrollHeaderDisplay =
        stickyDateLabel ||
        (firstTimestamp ? formatInterviewScrollHeader(firstTimestamp) : '');

    useLayoutEffect(() => {
        requestAnimationFrame(() => updateScrollDateLabel());
    }, [messageTimestamps, updateScrollDateLabel]);

    useEffect(() => {
        const el = threadScrollRef.current;
        if (!el) return;

        updateScrollDateLabel();
        el.addEventListener('scroll', updateScrollDateLabel, { passive: true });
        return () => el.removeEventListener('scroll', updateScrollDateLabel);
    }, [updateScrollDateLabel]);

    useEffect(() => {
        scrollToBottom();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, scrollDeps);

    return {
        threadScrollRef,
        scrollToBottom,
        scrollHeaderDisplay,
    };
}
